import { handleError } from '@/general/errorHandling'
import { IbePriceModelCode } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import { IDetailCruise, IRequest } from '@gecko-akh/pkg-cruise-api-types/src/vivien/pdf_offer'
import { cloneDeep } from 'lodash-es'
import { useDetailsStore, useFlexFlightStore, useFlightStore, usePriceStore, useRailStore } from '.'
import { Direction } from '../../bewotec/openapi'
import { generateRequestId } from '../../general/helper'
import { useCatalogStore } from '../catalog'
import { IPaxConfig } from '../catalog/interfaces'
import { useCruiseListStore } from '../cruise-list'
import { useFilterStore } from '../filter'
import { addTrackingEvent, buildCruiseViewTrackingEvent } from '../tracking/helper'
import { CruiseViewTrackingEventParams } from '../tracking/interface'
import { useUserStore } from '../user'
import { IFlightAvailabilities } from './../catalog/interfaces'
import { useCabinStore } from './cabin'
import { useCruiseStore } from './cruise'
import { convertIBEPriceModel } from './cruise/helper'
import { hasFlexFlight } from './flex-flight/helper'
import { detailsHelper } from './helper'

export const fetchDetails = async (cruiseId: string, paxConfig: IPaxConfig, flights: IFlightAvailabilities) => {
  const catalogStore = useCatalogStore()
  const filterStore = useFilterStore()
  const cabinStore = useCabinStore()
  const cruiseStore = useCruiseStore()
  const detailsStore = useDetailsStore()
  const flightStore = useFlightStore()
  const flexFlightStore = useFlexFlightStore()
  const railStore = useRailStore()
  const priceStore = usePriceStore()

  reset()

  cabinStore.type = ''
  cabinStore.category = null
  flexFlightStore.reset()
  detailsStore.paxConfig = cloneDeep(paxConfig)
  cruiseStore.requestId = generateRequestId('DET')

  const priceModel = filterStore.selectedPriceModel ?? detailsStore.firstAvailablePriceModel

  if (!priceModel) {
    handleError({ error: new Error('PriceModel is null'), origin: 'details - fetchDetails' })
    return
  }

  await cruiseStore.fetchCruise(cruiseId, priceModel).then(() => {
    if (!cruiseStore.cruise?.id) return

    preselectPriceModel()

    flexFlightStore.fetchAvailableGateways()

    preselectDetails(priceModel)

    const params: CruiseViewTrackingEventParams = {
      cruise: cruiseStore.cruise,
      route: cruiseStore.route,
      ship: cruiseStore.ship,
      cabin: cabinStore.cabin,
      flight: flightStore.selectedFlight,
      category: cabinStore.category,
      totalPrice: priceStore.total,
      mandatoryFlight: detailsStore.mandatoryFlightForPriceModel,
      cabinPrice: priceStore.cabinTotal,
      paxConfig: filterStore.paxConfig,
      transportationTotal: priceStore.transportationTotal,
      position: catalogStore.cruises.findIndex(cruise => cruise.id === cruiseStore.cruise?.id),
    }

    const event = buildCruiseViewTrackingEvent(params)
    addTrackingEvent(event, 'Cruise View Tracking Event')
  })

  if (!!cruiseStore.cruise?.id) {
    await railStore.fetchRail()
  }

  const railOptions = railStore.selectableRailOptions
  const mandatoryFlight: boolean = detailsStore.mandatoryFlightForPriceModel
  const hasRail =
    !('error' in railOptions) &&
    (!!railOptions?.[Direction.RT]?.length ||
      !!railOptions?.[Direction.R]?.length ||
      !!railOptions?.[Direction.O]?.length)
  const hasFlexFlights = !!cruiseStore.cruise?.hasFlexFlights
  let hasFlights = false

  for (const flight of Object.values(flights)) {
    if (flight.inbound || flight.outbound) {
      hasFlights = true
      break
    }
  }

  detailsStore.transportations = {
    Flug: hasFlights,
    Wunschflug: hasFlexFlights,
    Bahn: !mandatoryFlight && hasRail,
    Individuell: !mandatoryFlight,
  }

  detailsStore.transportation = mandatoryFlight ? 'Flug' : 'Individuell'
}

const preselectPriceModel = () => {
  const filterStore = useFilterStore()
  const detailsStore = useDetailsStore()

  const priceModel = filterStore.selectedPriceModel ?? detailsStore.firstAvailablePriceModel as IbePriceModelCode

  detailsStore.priceModel = priceModel
}

export const selectPriceModel = async (priceModel: IbePriceModelCode) => {
  const cruiseListStore = useCruiseListStore()
  const catalogStore = useCatalogStore()
  const cruiseStore = useCruiseStore()
  const detailsStore = useDetailsStore()
  const flexFlightStore = useFlexFlightStore()
  const flightStore = useFlightStore()
  const railStore = useRailStore()

  cruiseStore.requestId = generateRequestId('DET')
  detailsStore.priceModel = priceModel
  
  await cruiseStore.fetchCruise(cruiseListStore.selectedEntryId, priceModel).then(() => {
    preselectDetails(priceModel)
  })
  
  flightStore.railDirectionRNF = 'none'
  flightStore.resetRail()

  const isFlexFlightAllowed = hasFlexFlight(catalogStore.cruises, cruiseStore.cruise!)
  
  flexFlightStore.isFlexFlightSearchEnabled = isFlexFlightAllowed
  flexFlightStore.reset()

  if (isFlexFlightAllowed) {
    flexFlightStore.fetchAvailableGateways()
  }

  detailsStore.transportation = detailsStore.mandatoryFlightForPriceModel ? 'Flug' : 'Individuell'

  railStore.fetchRail()
}

const preselectDetails = async (priceModel: IbePriceModelCode) => {
  const cabinStore = useCabinStore()
  const flightStore = useFlightStore()

  cabinStore.type = cabinStore.cabinTypes[0]
  cabinStore.preselectCategory()

  await Promise.all([
    cabinStore.fetchCabins().then(() => {
      cabinStore.preselectCabin()
    }),

    flightStore.fetchFlights(priceModel).then(() => {
      flightStore.preselectFlight()
      flightStore.preselectRailOption()
    }),
  ])
}

export const reset = () => {
  const detailsStore = useDetailsStore()

  detailsStore.paxConfig = null
  detailsStore.priceModel = undefined
  detailsStore.mandatoryFlightForPriceModel = false
  detailsStore.transportation = 'Individuell'
  detailsStore.transportations = {
    Flug: true,
    Wunschflug: false,
    Bahn: false,
    Individuell: true,
  }
}

export const getOfferRequestForVivien = (): IRequest => {
  const userStore = useUserStore()
  const detailsStore = useDetailsStore()
  const priceStore = usePriceStore()
  const flightStore = useFlightStore()
  const flexFlightStore = useFlexFlightStore()
  const cruiseStore = useCruiseStore()
  const cabinStore = useCabinStore()

  const isFlexFlight = detailsStore.transportation === 'Wunschflug'
  const flexFlight = flexFlightStore.selectedFlight
  const flight = flightStore.selectedFlight

  const offerRequest: IRequest = {
    agent: {
      name: userStore.agentName,
      mail: userStore.agentEmail,
    },
    agency: {
      id: userStore.agencyId,
      name: userStore.agencyName,
      mail: userStore.agencyEmail,
      phone: userStore.agencyPhone,
    },
    general: {
      pdfWithAgencyInformation: userStore.pdfWithAgencyInformation,
      priceModel: convertIBEPriceModel(detailsStore.priceModel!),
      transportation: detailsStore.transportation,
      cabinLabel: priceStore.cabinLabelPDF,
      extras: priceStore.extrasForCabin,
    },
    // TODO: clean up unused properties in Vivien
    cruise: { ...cruiseStore.cruise } as IDetailCruise,
    ship: cruiseStore.ship,
    route: {
      ports: cruiseStore.route?.ports,
      name: cruiseStore.route?.name,
      image: cruiseStore.route?.mapImageUrl,
      segments: cruiseStore.route?.segments,
      germanDeparture: !!cruiseStore.route?.germanDeparture,
    },
    cabin: {
      type: cabinStore.type,
      cabinCategory: cabinStore.cabin?.cabinCategory,
      id: cabinStore.cabin?.id,
      position: cabinStore.cabin?.position,
      image: cabinStore.category?.image,
    },
    outboundFlight: detailsHelper.getFlightObjectForVivien(
      isFlexFlight ? flexFlight?.outboundFlight : flight?.outboundFlight,
      true
    ),
    inboundFlight: detailsHelper.getFlightObjectForVivien(
      isFlexFlight ? flexFlight?.inboundFlight : flight?.inboundFlight,
      false
    ),
    serviceIncluded: isFlexFlight ? flexFlightStore.serviceIncluded : flightStore.serviceIncluded,
    priceInformation: {
      cabinItems: priceStore.cabinItems,
      cabinTotal: priceStore.cabinTotal,
      transportationLabel: priceStore.transportationLabel,
      transportationItems: priceStore.transportationItems,
      transportationTotal: priceStore.transportationTotal,
      total: priceStore.total,
      hasBaby: !!detailsStore.paxConfig?.babies,
    },
  }

  return offerRequest
}

export default {
  fetchDetails,
  selectPriceModel,
  getOfferRequestForVivien,
}

// ---------------- Export for testing purposes only ----------------

export const exportForTesting = {
  reset,
  preselectDetails,
  preselectPriceModel,
}
